import { createFeature, createReducer, on } from '@ngrx/store';

import { HelpSupportTicketsActions } from './help-support-tickets.actions';
import { initialHelpSupportTicketsState } from './help-support-tickets.state';

export const helpSupportTicketsFeature = createFeature({
  name: 'helpSupportTickets',
  reducer: createReducer(
    initialHelpSupportTicketsState,
    on(HelpSupportTicketsActions.loadList, (state, { query }) => ({
      ...state,
      loadingList: true,
      error: null,
      listError: null,
      currentPage: query.page,
      pageSize: query.pageSize,
      listQuery: query,
    })),
    on(
      HelpSupportTicketsActions.loadListSuccess,
      (state, { rows, total, page, pageSize, stats }) => ({
        ...state,
        rows,
        stats,
        totalTickets: total,
        currentPage: page,
        pageSize,
        loadingList: false,
        listError: null,
      }),
    ),
    on(HelpSupportTicketsActions.loadListFailure, (state, { error }) => ({
      ...state,
      rows: [],
      totalTickets: 0,
      loadingList: false,
      error,
      listError: error,
    })),
    on(HelpSupportTicketsActions.loadCategories, (state) => ({
      ...state,
      loadingCategories: true,
    })),
    on(HelpSupportTicketsActions.loadCategoriesSuccess, (state, { categories }) => ({
      ...state,
      categories,
      loadingCategories: false,
    })),
    on(HelpSupportTicketsActions.loadCategoriesFailure, (state, { error }) => ({
      ...state,
      loadingCategories: false,
      error,
    })),
    on(HelpSupportTicketsActions.loadDetail, (state) => ({
      ...state,
      loadingDetail: true,
      error: null,
    })),
    on(HelpSupportTicketsActions.loadDetailSuccess, (state, { ticket }) => ({
      ...state,
      selectedTicket: ticket,
      loadingDetail: false,
    })),
    on(HelpSupportTicketsActions.loadDetailFailure, (state, { error }) => ({
      ...state,
      selectedTicket: null,
      loadingDetail: false,
      error,
    })),
    on(HelpSupportTicketsActions.createTicket, (state) => ({
      ...state,
      submitting: true,
      error: null,
      successMessage: null,
    })),
    on(HelpSupportTicketsActions.createTicketSuccess, (state, { ticket, message }) => ({
      ...state,
      submitting: false,
      selectedTicket: ticket,
      successMessage: message,
    })),
    on(HelpSupportTicketsActions.createTicketFailure, (state, { error }) => ({
      ...state,
      submitting: false,
      error,
    })),
    on(HelpSupportTicketsActions.createMessage, (state) => ({
      ...state,
      submitting: true,
      error: null,
      successMessage: null,
    })),
    on(HelpSupportTicketsActions.createMessageSuccess, (state, { ticket, toast }) => ({
      ...state,
      submitting: false,
      successMessage: toast,
      selectedTicket: ticket,
    })),
    on(HelpSupportTicketsActions.createMessageFailure, (state, { error }) => ({
      ...state,
      submitting: false,
      error,
    })),
    on(
      HelpSupportTicketsActions.receiveRealtimeMessage,
      (state, { ticketId, message, ticketStatus }) => {
        if (!state.selectedTicket || state.selectedTicket.ticketId !== ticketId) {
          return state;
        }
        const messages = state.selectedTicket.messages.some((item) => item.id === message.id)
          ? state.selectedTicket.messages
          : [...state.selectedTicket.messages, message];
        return {
          ...state,
          selectedTicket: {
            ...state.selectedTicket,
            messages,
            status: (ticketStatus ??
              state.selectedTicket.status) as typeof state.selectedTicket.status,
          },
        };
      },
    ),
    on(HelpSupportTicketsActions.createInternalNote, (state) => ({
      ...state,
      submitting: true,
      error: null,
      successMessage: null,
    })),
    on(HelpSupportTicketsActions.createInternalNoteSuccess, (state, { note, toast }) => ({
      ...state,
      submitting: false,
      successMessage: toast,
      selectedTicket: state.selectedTicket
        ? {
            ...state.selectedTicket,
            internalNotes: [...state.selectedTicket.internalNotes, note],
          }
        : state.selectedTicket,
    })),
    on(HelpSupportTicketsActions.createInternalNoteFailure, (state, { error }) => ({
      ...state,
      submitting: false,
      error,
    })),
    on(HelpSupportTicketsActions.changeAssignee, (state) => ({
      ...state,
      submitting: true,
      error: null,
      successMessage: null,
    })),
    on(HelpSupportTicketsActions.changeAssigneeSuccess, (state, { ticket, toast }) => ({
      ...state,
      submitting: false,
      selectedTicket: ticket,
      successMessage: toast,
    })),
    on(HelpSupportTicketsActions.changeAssigneeFailure, (state, { error }) => ({
      ...state,
      submitting: false,
      error,
    })),
    on(HelpSupportTicketsActions.changeCategory, (state) => ({
      ...state,
      submitting: true,
      error: null,
      successMessage: null,
    })),
    on(HelpSupportTicketsActions.changeCategorySuccess, (state, { ticket, toast }) => ({
      ...state,
      submitting: false,
      selectedTicket: ticket,
      successMessage: toast,
    })),
    on(HelpSupportTicketsActions.changeCategoryFailure, (state, { error }) => ({
      ...state,
      submitting: false,
      error,
    })),
    on(HelpSupportTicketsActions.changePriority, (state) => ({
      ...state,
      submitting: true,
      error: null,
      successMessage: null,
    })),
    on(HelpSupportTicketsActions.changePrioritySuccess, (state, { ticket, toast }) => ({
      ...state,
      submitting: false,
      selectedTicket: ticket,
      successMessage: toast,
    })),
    on(HelpSupportTicketsActions.changePriorityFailure, (state, { error }) => ({
      ...state,
      submitting: false,
      error,
    })),
    on(HelpSupportTicketsActions.assignToMe, (state) => ({
      ...state,
      submitting: true,
      error: null,
      successMessage: null,
    })),
    on(HelpSupportTicketsActions.assignToMeSuccess, (state, { ticket, toast }) => ({
      ...state,
      submitting: false,
      selectedTicket: ticket,
      successMessage: toast,
    })),
    on(HelpSupportTicketsActions.assignToMeFailure, (state, { error }) => ({
      ...state,
      submitting: false,
      error,
    })),
    on(HelpSupportTicketsActions.closeTicket, (state) => ({
      ...state,
      submitting: true,
      error: null,
      successMessage: null,
    })),
    on(HelpSupportTicketsActions.closeTicketSuccess, (state, { ticket, toast }) => ({
      ...state,
      submitting: false,
      selectedTicket: ticket,
      successMessage: toast,
    })),
    on(HelpSupportTicketsActions.closeTicketFailure, (state, { error }) => ({
      ...state,
      submitting: false,
      error,
    })),
    on(HelpSupportTicketsActions.reopenTicket, (state) => ({
      ...state,
      submitting: true,
      error: null,
      successMessage: null,
    })),
    on(HelpSupportTicketsActions.reopenTicketSuccess, (state, { ticket, toast }) => ({
      ...state,
      submitting: false,
      selectedTicket: ticket,
      successMessage: toast,
    })),
    on(HelpSupportTicketsActions.reopenTicketFailure, (state, { error }) => ({
      ...state,
      submitting: false,
      error,
    })),
    on(HelpSupportTicketsActions.clearError, (state) => ({
      ...state,
      error: null,
      listError: null,
    })),
    on(HelpSupportTicketsActions.clearSuccessMessage, (state) => ({
      ...state,
      successMessage: null,
    })),
    on(HelpSupportTicketsActions.clearSelectedTicket, (state) => ({
      ...state,
      selectedTicket: null,
    })),
  ),
});

export const {
  name: helpSupportTicketsFeatureKey,
  reducer: helpSupportTicketsReducer,
  selectRows,
  selectStats,
  selectCategories,
  selectTotalTickets,
  selectSelectedTicket,
  selectLoadingList,
  selectLoadingDetail,
  selectLoadingCategories,
  selectSubmitting,
  selectError,
  selectListError,
  selectSuccessMessage,
  selectCurrentPage,
  selectPageSize,
  selectListQuery,
} = helpSupportTicketsFeature;
